import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  type PricingConfigResponse,
  type PricingToday,
  fetchPricingConfig,
  fetchPricingToday,
  getSession,
} from "../api";
import { EarningsHistory } from "../components/EarningsHistory";
import { SavingsCard } from "../components/SavingsCard";
import { PriceLine } from "../components/PriceLine";
import { formatPricePerKwh } from "../format";
import { ScreenHeader, SectionTitle, Skeleton } from "../components/ui";
import {
  getPriceIncludesTax,
} from "../prefs";

const POLL_MS = 5 * 60_000;   // prices change hourly; 5 min poll is plenty
const DAYS_KEY = "prijzen:days";
// Window choices: today+tomorrow (the known auction), or further with
// the AI price forecast drawn lighter (2026-10-30).
const DAY_OPTIONS: { days: number; label: string }[] = [
  { days: 2, label: "2 dagen" },
  { days: 5, label: "5 dagen" },
  { days: 14, label: "2 weken" },
];
function readDays(): number {
  try {
    const v = Number(localStorage.getItem(DAYS_KEY));
    return DAY_OPTIONS.some((o) => o.days === v) ? v : 2;
  } catch {
    return 2;
  }
}

/**
 * Prijzen tab, money first: saldo + earnings-over-time on top, the
 * price chart below. The chart shows wholesale EPEX (default) or
 * EPEX + margin + tax (localStorage pref toggled from /account); only
 * dynamic contracts benefit, fixed-tariff always shows raw EPEX.
 * docs/customer-app.md > Front-end > Prijzen tab
 */
export function Prijzen() {
  const navigate = useNavigate();
  const session = getSession();
  const [pricing, setPricing] = useState<PricingToday | null>(null);
  const [config, setConfig] = useState<PricingConfigResponse | null>(null);
  const [includesTax, setIncludesTax] = useState<boolean>(getPriceIncludesTax());
  // Resolution lives in localStorage; we read it lazily on every poll
  // so the same poll-fn picks up flips. The single bit of derived state
  // below ("the chart caption") reads `pricing.resolution` directly so
  // it's always in sync with what the user is actually looking at.
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState<number>(readDays);
  const daysRef = useRef(days);
  const seq = useRef(0);
  const hasPricing = useRef(false);

  useEffect(() => {
    if (!session) {
      navigate("/login");
      return;
    }
    let cancelled = false;

    async function poll() {
      const my = ++seq.current;
      try {
        // Read the pref live each tick - switching the toggle in
        // /account flips the fetched series on the very next poll
        // (and the explicit refetch in the change-listener below
        // makes the swap feel instant).
        // Prijzen-tab staat VAST op kwartieren: sinds 2025 publiceert
        // de NL day-ahead-markt per 15 min en inmiddels rekenen alle
        // NL-leveranciers zo af - een uurgemiddelde verbergt precies de
        // pieken en dalen waar deze tab over gaat. De account-instelling
        // regelt nog wél de plan-/prognosegrafiek (daar is uurgrofheid
        // vaak rustiger leesbaar).
        const v = await fetchPricingToday("15min", daysRef.current);
        if (!cancelled && my === seq.current) {
          setPricing(v);
          hasPricing.current = true;
          setError(null);            // hersteld -> balk weg
        }
      } catch (e) {
        // Blip bij hervatten mag geen alarm zijn zolang er prijzen staan.
        if (!cancelled && !hasPricing.current) {
          setError(e instanceof Error ? e.message : String(e));
        }
      }
    }
    poll();
    const id = window.setInterval(poll, POLL_MS);
    // The window picker refetches at once (same poll, new `days`).
    function onDays() { poll(); }
    window.addEventListener("prijzen:days", onDays);

    // Pricing config is needed to compute "all-in" prices when the tax
    // toggle is on. Fetched once - changes via /account roundtrip
    // through the same listener below.
    fetchPricingConfig().then((c) => {
      if (!cancelled) setConfig(c);
    }).catch(() => {/* not fatal - chart still renders with wholesale */});

    // Listen for the tax-toggle event so flipping the switch in /account
    // updates this tab without a reload.
    function onTaxPref() {
      setIncludesTax(getPriceIncludesTax());
    }
    window.addEventListener("prefs:price-includes-tax", onTaxPref);

    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.removeEventListener("prefs:price-includes-tax", onTaxPref);
      window.removeEventListener("prijzen:days", onDays);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Compute the displayed price series - wholesale or all-in. Only
  // dynamic contracts get the all-in adjustment; fixed contracts pay
  // their flat tariff regardless of EPEX (toggle is a no-op for them).
  //
  // All-in formula mirrors customer_app/pricing.py exactly:
  //   import_price = (epex + margin + tax) × (1 + vat_pct)
  // So the per-bar transform is NOT a flat surcharge - it's a linear
  // function of epex. Each bar's height changes by (margin+tax)*1.21
  // plus the negative-price case where 0.21*epex matters.
  const showAllIn =
    includesTax &&
    config?.effective?.contract === "dynamic";
  const cfg = config?.effective;
  const displayPrices = useMemo(() => {
    if (!pricing) return [];
    if (!showAllIn || !cfg) return pricing.prices_eur_per_kwh;
    const margin = cfg.supplier_margin_eur_per_kwh;
    const tax = cfg.tax_eur_per_kwh;
    const vat = 1 + cfg.vat_pct;
    return pricing.prices_eur_per_kwh.map((p) =>
      p === null ? null : (p + margin + tax) * vat,
    );
  }, [pricing, showAllIn, cfg]);

  const nowPrice =
    pricing && displayPrices[pricing.current_hour_idx];
  function pickDays(d: number) {
    daysRef.current = d;
    setDays(d);
    try { localStorage.setItem(DAYS_KEY, String(d)); } catch { /* private mode */ }
    window.dispatchEvent(new Event("prijzen:days"));
  }
  const forecastFrom = pricing?.forecast_from ?? null;
  // Sell side (2026-10-04): a market-price contract (no saldering) pays
  // EPEX for export, far below the all-in buy price - show it as its
  // own curve so "duur uur" and "goed moment om terug te leveren" stop
  // being the same thing. Saldering: sell = buy, nothing extra to show.
  // Flat: one number, a sentence is enough.
  const exportPrices = pricing?.export_prices_eur_per_kwh ?? null;
  const exportMode = pricing?.export_mode ?? null;
  const showExport = exportMode === "dynamic" && !!exportPrices;
  const nowExport = showExport && pricing ? exportPrices[pricing.current_hour_idx] : null;

  return (
    <div className="tab-enter stagger px-4 pt-6 pb-28 max-w-3xl mx-auto flex flex-col gap-5 lg:max-w-4xl lg:px-8 lg:pt-10 lg:pb-12">
      <ScreenHeader title="Prijzen" subtitle="Opbrengst & stroomprijs" />
      {error && (
        <div className="text-sm text-danger bg-danger-soft rounded-xl p-3 border border-card-border">
          {error}
        </div>
      )}

      {/* Besparing boven (money-first), dan de prijsgrafiek, en het
          saldo onderaan - de prijs is de context voor beide
          geldblokken (Stefan, 18-08). */}
      <SavingsCard />

      <section className="flex flex-col gap-3">
        <header className="flex items-baseline justify-between gap-2 px-1">
          <SectionTitle>Stroomprijs vandaag</SectionTitle>
          {nowPrice != null && (
            <span className="display-num text-2xl sm:text-xl text-ink">
              {formatPricePerKwh(nowPrice)}
            </span>
          )}
        </header>

        <div className="flex gap-1.5 px-1" role="group" aria-label="Periode">
          {DAY_OPTIONS.map((o) => (
            <button
              key={o.days}
              type="button"
              onClick={() => pickDays(o.days)}
              aria-pressed={days === o.days}
              className={
                "rounded-full px-3 py-1 text-xs font-medium border transition-colors " +
                (days === o.days
                  ? "bg-accent text-white border-accent"
                  : "bg-card text-ink-mute border-card-border")
              }
            >
              {o.label}
            </button>
          ))}
        </div>

        {pricing ? (
          <div className="card p-3">
            <PriceLine
              t={pricing.t}
              prices={displayPrices}
              currentIdx={pricing.current_hour_idx}
              stepSec={pricing.resolution === "15min" ? 900 : 3600}
              secondary={showExport ? exportPrices : null}
              secondaryLabel="terugleveren"
              forecastFrom={forecastFrom}
            />
          </div>
        ) : (
          <Skeleton className="h-56" />
        )}

        <p className="text-xs text-ink-mute leading-relaxed">
          {showAllIn
            ? "Prijzen inclusief leveringsmarge en energiebelasting (totaalprijs uit uw contract). Schakel om in Account → Energiecontract."
            : "Inkoopprijs op de groothandelsmarkt (EPEX/ENTSO-E, NL), exclusief leveringsmarge en energiebelasting. Wat u daadwerkelijk betaalt staat in uw contract."}
          {pricing?.resolution === "15min" && (
            <> Per kwartier (NL day-ahead is sinds 2025 MTU-15).</>
          )}
          {forecastFrom != null && (
            <>
              {" "}De <strong>lichter gekleurde</strong> lijn vanaf{" "}
              {new Date(forecastFrom * 1000).toLocaleDateString("nl-NL", { weekday: "long" })}
              {" "}is de AI-prijsverwachting: nog niet vastgesteld, als richting voor de dagen erna.
            </>
          )}
          {showExport && (
            <>
              {" "}De grijze lijn is de <strong>terugleverprijs</strong>
              {nowExport != null && <> (nu {formatPricePerKwh(nowExport)})</>}:
              wat u ontvangt per kWh naar het net, de marktprijs
              {(config?.effective?.sun_bonus_pct ?? 0) > 0 && (
                <> plus overdag {Math.round((config?.effective?.sun_bonus_pct ?? 0) * 100)}% zonnebonus</>
              )}
              , zonder energiebelasting. Uw contract saldeert niet.
            </>
          )}
        </p>
      </section>

      {exportMode === "flat" && config?.effective && (
        <p className="text-xs text-ink-mute leading-relaxed px-1">
          Teruglevering: vaste vergoeding van{" "}
          {formatPricePerKwh(config.effective.feed_in_tariff_eur_per_kwh)}.
        </p>
      )}

      <EarningsHistory />
    </div>
  );
}
